import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import { PlusOutlined } from '@ant-design/icons';
import { remove, uniq } from 'lodash';
import Tag from 'antd/lib/tag';
import { colorWithAlpha, rwColors } from '../styles/globalStyles';
import { selectAuthCredentials } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import * as API from '../../api';
import { OrgComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { emailValidationRules } from '../../helpers/formHelpers';

interface InviteTeamModalProps extends OrgComponentProps {
    open: boolean;
    onCancel: () => void;
}

const InviteTeamModal: React.FC<InviteTeamModalProps> = (props: InviteTeamModalProps) => {
    const [form] = Form.useForm();
    const [emailsToInvite, setEmailsToInvite] = React.useState<string[]>([]);
    const [sendEmailsIsLoading, setSendEmailsIsLoading] = React.useState(false);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const addEmailToList = (values) => {
        const trimmedValue = values.email?.trim();

        if (!trimmedValue) {
            return;
        }

        if (trimmedValue === 'adrian@runway.team') {
            globalMessage.info("ceci n'est pas un œuf de Pâques");
            globalMessage.info('È ora il 2023 e questo non è ancora un uovo di Pasqua.');
            globalMessage.info('Τώρα είναι το 2024 και αυτό δεν είναι ακόμα ένα αυγό του Πάσχα.');
            globalMessage.info('今は2025年で、これはまだイースターエッグではありません。');
        }

        const updatedEmailsToInvite = [...emailsToInvite, trimmedValue];

        setEmailsToInvite(uniq(updatedEmailsToInvite));

        form.setFieldsValue({ email: null });
    };

    const removeEmailFromList = (email) => {
        const updatedEmailsToInvite = [...emailsToInvite];

        remove(updatedEmailsToInvite, (inviteEmail) => inviteEmail === email);

        setEmailsToInvite(uniq(updatedEmailsToInvite));

        form.setFieldsValue({ email: null });
    };

    const sendEmailInvites = () => {
        setSendEmailsIsLoading(true);

        API.inviteTeam({ orgId: props.orgId, emails: emailsToInvite }, authCredentials)
            .then(() => {
                globalMessage.success('Invite emails sent!');

                props.onCancel();
                return;
            })
            .catch((e: Error) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => {
                setSendEmailsIsLoading(false);
            });
    };

    return (
        <Modal
            title="Invite your team"
            okButtonProps={{ disabled: emailsToInvite.length === 0, loading: sendEmailsIsLoading }}
            onOk={sendEmailInvites}
            okText="Send email invites"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
                setEmailsToInvite([]);
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>Send invites to your team to join your Runway organization.</Text>
                <Form onFinish={addEmailToList} initialValues={{ email: null }} style={{ marginTop: 24 }} form={form}>
                    <Space style={{ width: '100%' }} align="start">
                        <Form.Item validateTrigger="onFinish" name="email" rules={emailValidationRules(emailsToInvite)}>
                            <Input
                                placeholder={'email@yourdomain.com'}
                                width={430}
                                autoFocus
                                allowClear
                                autoComplete="off"
                            />
                        </Form.Item>
                        <Button onClick={() => form.submit()} size="middle" type="primary" icon={<PlusOutlined />}>
                            Add
                        </Button>
                    </Space>
                </Form>
                {emailsToInvite.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text>Inviting users:</Text>
                        <Space wrap style={{ marginTop: 12 }}>
                            {emailsToInvite.map((email) => {
                                return (
                                    <Tag
                                        key={email}
                                        closable
                                        onClose={() => removeEmailFromList(email)}
                                        color={colorWithAlpha(rwColors.primary, 0.8)}
                                    >
                                        {email}
                                    </Tag>
                                );
                            })}
                        </Space>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default InviteTeamModal;
